import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircle,
  Close,
  Computer,
  ContentCopy,
  Lock,
} from '@mui/icons-material';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { customFetcher } from '../hooks/useFetch';
import { prettyClientName } from '../core/clientNames';

interface PendingAuth {
  userCode: string;
  ClientName: string;
  Status: 'pending' | 'approved' | 'denied' | 'expired';
  IP?: string | null;
  UserAgent?: string | null;
  Created_at?: string;
  Expires_at?: string;
}

type Stage =
  | 'loading'
  | 'pending'
  | 'approved'
  | 'denied'
  | 'expired'
  | 'not-found'
  | 'error';

/**
 * Authorize-Page für den OAuth-Device-Flow (RFC 8628):
 * Plugins schicken den User auf `/authorize?code=<UserCode>`. Der bereits im
 * Web-Frontend eingeloggte User sieht hier, welches Plugin Zugriff möchte,
 * und kann „Erlauben" oder „Ablehnen" wählen. Das Plugin pollt im
 * Hintergrund und holt sich nach Approve den ausgestellten SessionToken ab.
 *
 * Wenn die URL ohne `?code=` aufgerufen wird, kann der User den Code aus
 * dem Plugin-Display manuell eingeben.
 */
export const Authorize: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const initialCode = (searchParams.get('code') || '').trim().toUpperCase();
  const [code, setCode] = useState(initialCode);
  const [pending, setPending] = useState<PendingAuth | null>(null);
  const [stage, setStage] = useState<Stage>(
    initialCode ? 'loading' : 'pending'
  );
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const fetchPending = useCallback(async (userCode: string) => {
    setStage('loading');
    setErrorMsg(null);
    try {
      const data = (await customFetcher({
        endpoint: `/oauth/device/${encodeURIComponent(userCode)}`,
      })) as PendingAuth;
      setPending(data);
      if (data.Status === 'approved') setStage('approved');
      else if (data.Status === 'denied') setStage('denied');
      else if (data.Status === 'expired') setStage('expired');
      else setStage('pending');
    } catch (e) {
      // 404 → unbekannt/abgelaufen, anderes → echter Fehler
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes('Network response was not ok')) {
        setStage('not-found');
      } else {
        setErrorMsg(msg);
        setStage('error');
      }
    }
  }, []);

  useEffect(() => {
    if (initialCode) void fetchPending(initialCode);
  }, [initialCode, fetchPending]);

  const handleApprove = async () => {
    if (!code) return;
    setBusy(true);
    setErrorMsg(null);
    try {
      await customFetcher({
        endpoint: `/oauth/device/${encodeURIComponent(code)}/approve`,
        method: 'POST',
      });
      setStage('approved');
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : 'Approve fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const handleDeny = async () => {
    if (!code) return;
    setBusy(true);
    setErrorMsg(null);
    try {
      await customFetcher({
        endpoint: `/oauth/device/${encodeURIComponent(code)}/deny`,
        method: 'POST',
      });
      setStage('denied');
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : 'Deny fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const handleManualLookup = () => {
    const cleaned = code.trim().toUpperCase();
    if (!cleaned) return;
    void fetchPending(cleaned);
  };

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 96px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Paper
        sx={{
          maxWidth: 520,
          width: '100%',
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Stack direction='row' alignItems='center' spacing={1.5}>
          <Lock color='primary' />
          <Typography variant='h6'>Plugin-Zugriff bestätigen</Typography>
        </Stack>

        {stage === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {(stage === 'pending' && !pending) || stage === 'not-found' ? (
          <>
            <Typography variant='body2' color='text.secondary'>
              {stage === 'not-found'
                ? 'Der eingegebene Code ist unbekannt oder abgelaufen.'
                : 'Bitte geben Sie den im Plugin angezeigten Code ein:'}
            </Typography>
            <TextField
              label='Code'
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder='K7M4-9XQR'
              autoFocus
              inputProps={{
                style: { letterSpacing: 2, fontFamily: 'monospace' },
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') handleManualLookup();
              }}
            />
            <Button
              variant='contained'
              onClick={handleManualLookup}
              disabled={!code.trim()}
            >
              Code prüfen
            </Button>
          </>
        ) : null}

        {pending && stage === 'pending' && (
          <>
            <Alert severity='info' icon={<Computer />}>
              <strong>{prettyClientName(pending.ClientName)}</strong> möchte auf
              Ihr DiNo-Konto zugreifen.
            </Alert>

            <Box
              sx={{
                bgcolor: 'action.hover',
                borderRadius: 1,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Stack direction='row' spacing={1} alignItems='center'>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ minWidth: 90 }}
                >
                  Code
                </Typography>
                <Chip
                  label={pending.userCode}
                  variant='outlined'
                  sx={{ fontFamily: 'monospace', letterSpacing: 1 }}
                  size='small'
                />
                <Button
                  size='small'
                  startIcon={<ContentCopy fontSize='inherit' />}
                  onClick={() =>
                    void navigator.clipboard.writeText(pending.userCode)
                  }
                >
                  Kopieren
                </Button>
              </Stack>
              {pending.IP && (
                <Stack direction='row' spacing={1}>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ minWidth: 90 }}
                  >
                    IP
                  </Typography>
                  <Typography variant='caption'>{pending.IP}</Typography>
                </Stack>
              )}
              {pending.UserAgent && (
                <Stack direction='row' spacing={1}>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ minWidth: 90 }}
                  >
                    Client
                  </Typography>
                  <Typography variant='caption' sx={{ wordBreak: 'break-all' }}>
                    {pending.UserAgent}
                  </Typography>
                </Stack>
              )}
              {pending.Expires_at && (
                <Stack direction='row' spacing={1}>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ minWidth: 90 }}
                  >
                    Gültig bis
                  </Typography>
                  <Typography variant='caption'>
                    {new Date(pending.Expires_at).toLocaleString('de-DE')}
                  </Typography>
                </Stack>
              )}
            </Box>

            <Typography variant='body2'>
              Bitte nur bestätigen, wenn Sie diese Anfrage soeben in Ihrem
              Plugin ausgelöst haben. Nach dem Bestätigen kann das Plugin in
              Ihrem Namen auf DiNo zugreifen, bis Sie die Verbindung wieder
              widerrufen.
            </Typography>

            {errorMsg && <Alert severity='error'>{errorMsg}</Alert>}

            <Stack direction='row' spacing={1} justifyContent='flex-end'>
              <Button
                onClick={handleDeny}
                disabled={busy}
                startIcon={<Close />}
                color='inherit'
              >
                Ablehnen
              </Button>
              <Button
                onClick={handleApprove}
                disabled={busy}
                variant='contained'
                startIcon={<CheckCircle />}
              >
                {busy ? 'Speichere…' : 'Erlauben'}
              </Button>
            </Stack>
          </>
        )}

        {stage === 'approved' && (
          <>
            <Alert severity='success' icon={<CheckCircle />}>
              Plugin-Zugriff erlaubt. Sie können dieses Fenster schließen — das
              Plugin holt sich den Token automatisch.
            </Alert>
            <Button onClick={() => navigate('/')}>Zurück zur Übersicht</Button>
          </>
        )}

        {stage === 'denied' && (
          <>
            <Alert severity='warning' icon={<Close />}>
              Sie haben den Plugin-Zugriff abgelehnt. Das Plugin wird beim
              nächsten Polling-Versuch eine entsprechende Antwort erhalten.
            </Alert>
            <Button onClick={() => navigate('/')}>Zurück zur Übersicht</Button>
          </>
        )}

        {stage === 'expired' && (
          <Alert severity='warning'>
            Die Anfrage ist abgelaufen. Bitte starten Sie den
            Verbindungs-Vorgang im Plugin neu.
          </Alert>
        )}

        {stage === 'error' && (
          <Alert severity='error'>
            {errorMsg || 'Es ist ein unbekannter Fehler aufgetreten.'}
          </Alert>
        )}
      </Paper>
    </Box>
  );
};
